import React, { useState, useEffect, useCallback } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const AdminMarksCardAssign = ({ onNavigate, userInfo = {} }) => {
    const [isMobile, setIsMobile] = useState(false);
    const [activeTab, setActiveTab] = useState('assign');

    // Departments
    const [departments, setDepartments] = useState([]);

    // Cascading filters
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedBatch, setSelectedBatch] = useState('');

    // Batch options (fetched dynamically)
    const [batches, setBatches] = useState([]);
    const [batchesLoading, setBatchesLoading] = useState(false);

    // Exam Subjects for selected batch
    const [examSubjects, setExamSubjects] = useState([]);
    const [subjectsLoading, setSubjectsLoading] = useState(false);

    // Teachers for department
    const [teachers, setTeachers] = useState([]);
    const [teachersLoading, setTeachersLoading] = useState(false);

    // Selected subject + teacher for assignment
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [selectedTeacher, setSelectedTeacher] = useState('');

    // Existing assignments
    const [assignments, setAssignments] = useState([]);
    const [assignmentsLoading, setAssignmentsLoading] = useState(false);

    // Submitting
    const [submitting, setSubmitting] = useState(false);

    // Total marks for assignment
    const [totalMarks, setTotalMarks] = useState('100');

    // Editing assignment
    const [editingAssignment, setEditingAssignment] = useState(null);
    const [editTeacher, setEditTeacher] = useState('');
    const [editTotalMarks, setEditTotalMarks] = useState('100');

    // ── Tab 2 State ──
    const [tab2Dept, setTab2Dept] = useState('');
    const [tab2Year, setTab2Year] = useState('');
    const [tab2Batch, setTab2Batch] = useState('');
    const [tab2Batches, setTab2Batches] = useState([]);
    const [tab2BatchesLoading, setTab2BatchesLoading] = useState(false);
    const [submissionStatus, setSubmissionStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(false);
    const [studentMarks, setStudentMarks] = useState(null);
    const [marksLoading, setMarksLoading] = useState(false);
    const [publishTitle, setPublishTitle] = useState('');
    const [publishing, setPublishing] = useState(false);

    const yearOptions = [
        { value: '2001', label: '1st Year' },
        { value: '2002', label: '2nd Year' },
        { value: '2003', label: '3rd Year' },
        { value: '2004', label: '4th Year' }
    ];

    // Responsive
    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    // Fetch departments
    useEffect(() => {
        const fetchDepartments = async () => {
            try {
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const res = await fetch(`${API_LINK}/api/admin/departments`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success) {
                    setDepartments(data.data || data.departments || []);
                }
            } catch (err) {
                console.error('Error fetching departments:', err);
            }
        };
        fetchDepartments();
    }, []);

    // Fetch batches when department + year changes
    useEffect(() => {
        const fetchBatches = async () => {
            if (!selectedDepartment || !selectedYear) {
                setBatches([]);
                return;
            }
            try {
                setBatchesLoading(true);
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const params = new URLSearchParams({ department: selectedDepartment, year: selectedYear });
                const res = await fetch(`${API_LINK}/api/admin/batches?${params}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                setBatches(data.success ? (data.data || []) : []);
            } catch (err) {
                console.error('Error fetching batches:', err);
                setBatches([]);
            } finally {
                setBatchesLoading(false);
            }
        };
        fetchBatches();
        setSelectedBatch('');
        setExamSubjects([]);
        setSelectedSubject(null);
    }, [selectedDepartment, selectedYear]);

    // Fetch exam subjects when batch selected
    useEffect(() => {
        const fetchExamSubjects = async () => {
            if (!selectedDepartment || !selectedYear || !selectedBatch) {
                setExamSubjects([]);
                return;
            }
            try {
                setSubjectsLoading(true);
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const params = new URLSearchParams({
                    department: selectedDepartment,
                    year: selectedYear,
                    batch: selectedBatch
                });
                const res = await fetch(`${API_LINK}/api/admin/exam-schedule?${params}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                setExamSubjects(data.success ? (data.data || []) : []);
            } catch (err) {
                console.error('Error fetching exam subjects:', err);
                setExamSubjects([]);
            } finally {
                setSubjectsLoading(false);
            }
        };
        fetchExamSubjects();
        setSelectedSubject(null);
        setSelectedTeacher('');
    }, [selectedDepartment, selectedYear, selectedBatch]);

    // Fetch teachers when department is selected
    useEffect(() => {
        const fetchTeachers = async () => {
            if (!selectedDepartment) {
                setTeachers([]);
                return;
            }
            try {
                setTeachersLoading(true);
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const res = await fetch(`${API_LINK}/api/admin/teachers-by-department?department=${encodeURIComponent(selectedDepartment)}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                setTeachers(data.success ? (data.data || []) : []);
            } catch (err) {
                console.error('Error fetching teachers:', err);
                setTeachers([]);
            } finally {
                setTeachersLoading(false);
            }
        };
        fetchTeachers();
    }, [selectedDepartment]);

    // Fetch existing assignments
    const fetchAssignments = useCallback(async () => {
        if (!selectedDepartment || !selectedYear || !selectedBatch) {
            setAssignments([]);
            return;
        }
        try {
            setAssignmentsLoading(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const params = new URLSearchParams({
                department: selectedDepartment,
                year: selectedYear,
                batch: selectedBatch
            });
            const res = await fetch(`${API_LINK}/api/admin/marks-teacher-assignments?${params}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setAssignments(data.success ? (data.data || []) : []);
        } catch (err) {
            console.error('Error fetching assignments:', err);
            setAssignments([]);
        } finally {
            setAssignmentsLoading(false);
        }
    }, [selectedDepartment, selectedYear, selectedBatch]);

    useEffect(() => {
        fetchAssignments();
    }, [fetchAssignments]);

    // Handle assign teacher
    const handleAssign = async () => {
        if (!selectedSubject || !selectedTeacher) {
            alert('Please select both a subject and a teacher.');
            return;
        }
        try {
            setSubmitting(true);
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const payload = {
                exam_schedule_id: selectedSubject.id,
                teacher_id: selectedTeacher,
                department: selectedDepartment,
                year: selectedYear,
                batch: selectedBatch,
                total_marks: parseInt(totalMarks) || 100
            };
            console.log('Sending assignment payload:', payload, 'selectedSubject:', selectedSubject);
            const res = await fetch(`${API_LINK}/api/admin/marks-teacher-assignment`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                alert(data.message || 'Teacher assigned successfully!');
                setSelectedSubject(null);
                setSelectedTeacher('');
                setTotalMarks('100');
                fetchAssignments();
            } else {
                alert('Failed: ' + (data.message || 'Unknown error'));
            }
        } catch (err) {
            console.error('Error assigning teacher:', err);
            alert('Error assigning teacher.');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete assignment
    const handleDeleteAssignment = async (id) => {
        if (!window.confirm('Remove this teacher assignment?')) return;
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const res = await fetch(`${API_LINK}/api/admin/marks-teacher-assignment/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                fetchAssignments();
            } else {
                alert('Failed to remove assignment.');
            }
        } catch (err) {
            console.error('Error removing assignment:', err);
        }
    };

    // Check if a subject is already assigned
    const getAssignmentForSubject = (subjectId) => {
        return assignments.find(a => a.exam_schedule_id === subjectId);
    };

    // Handle edit assignment
    const handleEditAssignment = async () => {
        if (!editingAssignment) return;
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const res = await fetch(`${API_LINK}/api/admin/marks-teacher-assignment/${editingAssignment.id}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ teacher_id: editTeacher || undefined, total_marks: parseInt(editTotalMarks) || 100 })
            });
            const data = await res.json();
            if (data.success) {
                alert('Assignment updated!');
                setEditingAssignment(null);
                fetchAssignments();
            } else { alert('Failed: ' + data.message); }
        } catch (err) {
            console.error(err);
            alert('Error updating assignment.');
        }
    };

    // ── Tab 2: Fetch batches ──
    useEffect(() => {
        if (!tab2Dept || !tab2Year) { setTab2Batches([]); return; }
        const fetchTab2Batches = async () => {
            setTab2BatchesLoading(true);
            try {
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const res = await fetch(`${API_LINK}/api/admin/batches?department=${encodeURIComponent(tab2Dept)}&year=${encodeURIComponent(tab2Year)}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success) setTab2Batches(data.data || []);
            } catch (err) { console.error(err); }
            setTab2BatchesLoading(false);
        };
        fetchTab2Batches();
    }, [tab2Dept, tab2Year]);

    // ── Tab 2: Fetch submission status + student marks when batch selected ──
    useEffect(() => {
        if (!tab2Dept || !tab2Year || !tab2Batch) return;
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        const fetchStatus = async () => {
            setStatusLoading(true);
            try {
                const res = await fetch(`${API_LINK}/api/admin/marks-submission-status?department=${encodeURIComponent(tab2Dept)}&year=${encodeURIComponent(tab2Year)}&batch=${encodeURIComponent(tab2Batch)}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success) setSubmissionStatus(data.data);
            } catch (err) { console.error(err); }
            setStatusLoading(false);
        };

        const fetchMarks = async () => {
            setMarksLoading(true);
            try {
                const res = await fetch(`${API_LINK}/api/admin/batch-student-marks?department=${encodeURIComponent(tab2Dept)}&year=${encodeURIComponent(tab2Year)}&batch=${encodeURIComponent(tab2Batch)}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success) setStudentMarks(data.data);
            } catch (err) { console.error(err); }
            setMarksLoading(false);
        };

        fetchStatus();
        fetchMarks();
    }, [tab2Dept, tab2Year, tab2Batch]);

    // ── Tab 2: Publish handler ──
    const handlePublish = async () => {
        if (!publishTitle.trim()) return;
        if (!window.confirm(`Publish marksheets for this batch with title "${publishTitle}"? Students will be able to download them.`)) return;
        setPublishing(true);
        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const res = await fetch(`${API_LINK}/api/admin/publish-marksheets`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ department: tab2Dept, year: tab2Year, batch: tab2Batch, title: publishTitle })
            });
            const data = await res.json();
            if (data.success) {
                alert(`✅ ${data.message}`);
                setPublishTitle('');
            } else {
                alert('Failed: ' + (data.message || 'Unknown error'));
            }
        } catch (err) {
            console.error(err);
            alert('Error publishing marksheets.');
        }
        setPublishing(false);
    };

    // ── Styles ──
    const cardStyle = {
        background: '#fff',
        borderRadius: '14px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 16px rgba(0,0,0,0.06)'
    };

    const selectStyle = {
        width: '100%',
        padding: '10px 14px',
        border: '1.5px solid #e0e0e0',
        borderRadius: '8px',
        fontSize: '0.9rem',
        outline: 'none',
        transition: 'border-color 0.2s',
        boxSizing: 'border-box',
        appearance: 'auto',
        background: '#fff'
    };

    const labelStyle = {
        display: 'block',
        fontSize: '0.78rem',
        fontWeight: 600,
        color: '#555',
        marginBottom: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.3px'
    };

    const tabBtnStyle = (active) => ({
        padding: '12px 28px',
        border: 'none',
        borderBottom: active ? '3px solid #238548' : '3px solid transparent',
        background: active ? '#f0faf3' : 'transparent',
        color: active ? '#238548' : '#777',
        fontWeight: active ? 700 : 500,
        fontSize: '0.95rem',
        cursor: 'pointer',
        transition: 'all 0.2s',
        borderRadius: '8px 8px 0 0'
    });

    const badgeStyle = (color = '#238548') => ({
        display: 'inline-block',
        background: color + '18',
        color: color,
        padding: '3px 10px',
        borderRadius: '12px',
        fontWeight: 600,
        fontSize: '0.78rem'
    });

    return (
        <Layout role="admin" onNavigate={onNavigate} activePage="admin-assign-marks" userInfo={userInfo}>
            {/* Banner */}
            <div style={{
                background: 'linear-gradient(135deg, #1a6834 0%, #43a863 50%, #7ed89b 100%)',
                color: '#fff',
                padding: '2rem 2.5rem',
                borderRadius: '14px',
                marginBottom: '1.5rem',
                boxShadow: '0 6px 24px rgba(35,133,72,0.25)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div style={{
                    position: 'absolute', top: -50, right: -30, width: 180, height: 180,
                    borderRadius: '50%', background: 'rgba(255,255,255,0.08)'
                }} />
                <div style={{
                    position: 'absolute', bottom: -40, right: 80, width: 120, height: 120,
                    borderRadius: '50%', background: 'rgba(255,255,255,0.06)'
                }} />
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.6rem', fontWeight: 700, position: 'relative' }}>
                    📋 Marks Assignment Management
                </h2>
                <p style={{ margin: 0, opacity: 0.9, fontSize: '0.95rem', position: 'relative' }}>
                    Assign teachers to subjects for each batch. Teachers can then enter marks from their portal.
                </p>
            </div>

            {/* Tabs */}
            <div style={{
                display: 'flex',
                borderBottom: '2px solid #e9ecef',
                marginBottom: '1.5rem',
                background: '#fff',
                borderRadius: '12px 12px 0 0',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
            }}>
                <button style={tabBtnStyle(activeTab === 'assign')} onClick={() => setActiveTab('assign')}>
                    📌 Assign Teacher
                </button>
                <button style={tabBtnStyle(activeTab === 'publish')} onClick={() => setActiveTab('publish')}>
                    📢 Confirm & Publish
                </button>
            </div>

            {activeTab === 'assign' && (
                <>
                    {/* ── Step 1: Select Department, Year, Batch ── */}
                    <div style={cardStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.2rem' }}>
                            <span style={{
                                background: '#238548', color: '#fff', width: 28, height: 28,
                                borderRadius: '50%', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem'
                            }}>1</span>
                            <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#333' }}>
                                Select Department, Year & Batch
                            </h3>
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                            gap: '1rem'
                        }}>
                            {/* Department */}
                            <div>
                                <label style={labelStyle}>Department *</label>
                                <select
                                    value={selectedDepartment}
                                    onChange={e => setSelectedDepartment(e.target.value)}
                                    style={selectStyle}
                                >
                                    <option value="">Select Department</option>
                                    {departments.map((d, i) => (
                                        <option key={i} value={d.value || d.department_name || d.name || d}>
                                            {d.label || d.department_name || d.name || d}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Year */}
                            <div>
                                <label style={labelStyle}>Year *</label>
                                <select
                                    value={selectedYear}
                                    onChange={e => setSelectedYear(e.target.value)}
                                    style={selectStyle}
                                >
                                    <option value="">Select Year</option>
                                    {yearOptions.map(y => (
                                        <option key={y.value} value={y.value}>{y.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Batch */}
                            <div>
                                <label style={labelStyle}>Batch *</label>
                                <select
                                    value={selectedBatch}
                                    onChange={e => setSelectedBatch(e.target.value)}
                                    style={selectStyle}
                                    disabled={!selectedDepartment || !selectedYear || batchesLoading}
                                >
                                    <option value="">
                                        {batchesLoading ? '⏳ Loading batches...'
                                            : (!selectedDepartment || !selectedYear) ? 'Select Dept & Year first'
                                                : batches.length === 0 ? 'No batches available'
                                                    : 'Select Batch'}
                                    </option>
                                    {batches.map((b, i) => <option key={i} value={b}>{b}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* ── Step 2: Exam Subjects ── */}
                    {selectedBatch && (
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.2rem' }}>
                                <span style={{
                                    background: '#238548', color: '#fff', width: 28, height: 28,
                                    borderRadius: '50%', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem'
                                }}>2</span>
                                <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#333' }}>
                                    Select Exam Subject
                                </h3>
                                <span style={{
                                    marginLeft: 'auto',
                                    ...badgeStyle('#555')
                                }}>
                                    {examSubjects.length} subject{examSubjects.length !== 1 ? 's' : ''} found
                                </span>
                            </div>

                            {subjectsLoading ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
                                    <p>Loading exam subjects...</p>
                                </div>
                            ) : examSubjects.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                                    <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📭</div>
                                    <p style={{ margin: 0, fontSize: '0.95rem' }}>
                                        No exam subjects found for this batch. Add subjects from the <strong>Exam Schedule</strong> page first.
                                    </p>
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '0.75rem' }}>
                                    {examSubjects.map(sub => {
                                        const existingAssignment = getAssignmentForSubject(sub.id);
                                        const isSelected = selectedSubject?.id === sub.id;

                                        return (
                                            <div
                                                key={sub.id}
                                                onClick={() => setSelectedSubject(sub)}
                                                style={{
                                                    border: isSelected ? '2px solid #238548' : '1.5px solid #e9ecef',
                                                    borderRadius: '12px',
                                                    padding: '1rem 1.2rem',
                                                    cursor: 'pointer',
                                                    background: isSelected ? '#f0faf3' : '#fafafa',
                                                    transition: 'all 0.2s',
                                                    position: 'relative'
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
                                                    <span style={badgeStyle('#238548')}>
                                                        {sub.subject_code}
                                                    </span>
                                                    {isSelected && (
                                                        <span style={{
                                                            background: '#238548', color: '#fff',
                                                            padding: '2px 8px', borderRadius: '10px',
                                                            fontSize: '0.7rem', fontWeight: 600
                                                        }}>
                                                            ✓ Selected
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{
                                                    fontWeight: 600, fontSize: '0.95rem', color: '#333',
                                                    marginBottom: '0.3rem'
                                                }}>
                                                    {sub.subject_name}
                                                </div>
                                                <div style={{ fontSize: '0.8rem', color: '#888' }}>
                                                    {sub.exam_date
                                                        ? new Date(sub.exam_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                                                        : 'No date set'}
                                                    {sub.exam_time ? ` • ${sub.exam_time}` : ''}
                                                </div>

                                                {existingAssignment && (
                                                    <div style={{
                                                        marginTop: '0.5rem',
                                                        padding: '6px 10px',
                                                        background: '#fff7ed',
                                                        borderRadius: '8px',
                                                        border: '1px solid #fed7aa',
                                                        fontSize: '0.78rem',
                                                        color: '#9a3412'
                                                    }}>
                                                        👤 Assigned to: <strong>{existingAssignment.teacher_name || 'Unknown'}</strong>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Step 3: Select Teacher & Assign ── */}
                    {selectedSubject && (
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.2rem' }}>
                                <span style={{
                                    background: '#238548', color: '#fff', width: 28, height: 28,
                                    borderRadius: '50%', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem'
                                }}>3</span>
                                <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#333' }}>
                                    Assign Teacher to <span style={{ color: '#238548' }}>{selectedSubject.subject_name}</span>
                                </h3>
                            </div>

                            <div style={{
                                background: '#f8fdf9',
                                borderRadius: '10px',
                                padding: '1rem 1.2rem',
                                border: '1px solid #d4edda',
                                marginBottom: '1rem',
                                fontSize: '0.85rem'
                            }}>
                                <strong>Subject:</strong> {selectedSubject.subject_code} – {selectedSubject.subject_name} &nbsp;|&nbsp;
                                <strong>Batch:</strong> {selectedBatch} &nbsp;|&nbsp;
                                <strong>Dept:</strong> {selectedDepartment}
                            </div>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr',
                                gap: '1rem',
                                alignItems: 'end'
                            }}>
                                <div>
                                    <label style={labelStyle}>Select Teacher *</label>
                                    <select
                                        value={selectedTeacher}
                                        onChange={e => setSelectedTeacher(e.target.value)}
                                        style={selectStyle}
                                        disabled={teachersLoading}
                                    >
                                        <option value="">
                                            {teachersLoading ? '⏳ Loading teachers...'
                                                : teachers.length === 0 ? 'No teachers in this department'
                                                    : 'Choose a teacher'}
                                        </option>
                                        {teachers.map(t => (
                                            <option key={t.id} value={t.id}>
                                                {t.name} {t.designation ? `(${t.designation})` : ''} {t.email ? `— ${t.email}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={labelStyle}>Total Marks *</label>
                                    <input
                                        type="number"
                                        value={totalMarks}
                                        onChange={e => setTotalMarks(e.target.value)}
                                        placeholder="100"
                                        min="1"
                                        style={{ ...selectStyle, textAlign: 'center', fontWeight: 600, fontSize: '1rem' }}
                                    />
                                </div>
                                <div>
                                    <button
                                        onClick={handleAssign}
                                        disabled={!selectedTeacher || submitting}
                                        style={{
                                            width: '100%',
                                            padding: '12px 24px',
                                            background: !selectedTeacher ? '#ccc' : 'linear-gradient(135deg, #238548, #2ea35e)',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '10px',
                                            fontWeight: 700,
                                            fontSize: '0.9rem',
                                            cursor: !selectedTeacher ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.2s',
                                            boxShadow: selectedTeacher ? '0 4px 12px rgba(35,133,72,0.3)' : 'none',
                                            opacity: submitting ? 0.7 : 1
                                        }}
                                    >
                                        {submitting ? '⏳ Assigning...' : '✅ Assign Teacher'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Current Assignments Table ── */}
                    {selectedBatch && (
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#333' }}>
                                    📑 Current Assignments
                                    <span style={{
                                        ...badgeStyle('#555'),
                                        marginLeft: '0.5rem'
                                    }}>
                                        {assignments.length}
                                    </span>
                                </h3>
                            </div>

                            {assignmentsLoading ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                                    ⏳ Loading assignments...
                                </div>
                            ) : assignments.length === 0 ? (
                                <div style={{
                                    textAlign: 'center', padding: '2.5rem', color: '#999',
                                    background: '#fafafa', borderRadius: '10px'
                                }}>
                                    <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📋</div>
                                    <p style={{ fontSize: '0.95rem', margin: 0 }}>
                                        No teacher assignments yet for this batch. Select a subject above and assign a teacher.
                                    </p>
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                        <thead>
                                            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                                                <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 600, color: '#555' }}>#</th>
                                                <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 600, color: '#555' }}>Subject</th>
                                                <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 600, color: '#555' }}>Subject Code</th>
                                                <th style={{ padding: '12px 10px', textAlign: 'left', fontWeight: 600, color: '#555' }}>Teacher</th>
                                                <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 600, color: '#555' }}>Total Marks</th>
                                                <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 600, color: '#555' }}>Status</th>
                                                <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 600, color: '#555' }}>Assigned On</th>
                                                <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 600, color: '#555' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assignments.map((a, idx) => (
                                                <tr
                                                    key={a.id}
                                                    style={{
                                                        borderBottom: '1px solid #f0f0f0',
                                                        transition: 'background 0.15s'
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#f8fdf9'}
                                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                >
                                                    <td style={{ padding: '12px 10px', color: '#888' }}>{idx + 1}</td>
                                                    <td style={{ padding: '12px 10px', fontWeight: 500 }}>
                                                        {a.subject_name || '—'}
                                                    </td>
                                                    <td style={{ padding: '12px 10px' }}>
                                                        <span style={badgeStyle('#238548')}>
                                                            {a.subject_code || '—'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px 10px' }}>
                                                        <div style={{ fontWeight: 500 }}>{a.teacher_name || '—'}</div>
                                                        {a.teacher_designation && (
                                                            <div style={{ fontSize: '0.75rem', color: '#888' }}>{a.teacher_designation}</div>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                                                        <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#238548' }}>{a.total_marks || 100}</span>
                                                    </td>
                                                    <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                                                        <span style={badgeStyle(
                                                            a.status === 'completed' ? '#238548'
                                                                : a.status === 'in_progress' ? '#f59e0b'
                                                                    : '#6366f1'
                                                        )}>
                                                            {a.status === 'completed' ? '✅ Completed'
                                                                : a.status === 'in_progress' ? '🔄 In Progress'
                                                                    : '📌 Assigned'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px 10px', textAlign: 'center', whiteSpace: 'nowrap', color: '#666', fontSize: '0.8rem' }}>
                                                        {a.assigned_at ? new Date(a.assigned_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                                    </td>
                                                    <td style={{ padding: '12px 10px', textAlign: 'center', display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                                        <button
                                                            onClick={() => { setEditingAssignment(a); setEditTeacher(a.teacher_id || ''); setEditTotalMarks(String(a.total_marks || 100)); }}
                                                            style={{
                                                                background: '#dbeafe', color: '#2563eb', border: '1px solid #bfdbfe',
                                                                padding: '5px 14px', borderRadius: '6px', fontWeight: 500,
                                                                cursor: 'pointer', fontSize: '0.78rem', transition: 'all 0.15s'
                                                            }}
                                                        >
                                                            ✏️ Edit
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteAssignment(a.id)}
                                                            style={{
                                                                background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca',
                                                                padding: '5px 14px', borderRadius: '6px', fontWeight: 500,
                                                                cursor: 'pointer', fontSize: '0.78rem', transition: 'all 0.15s'
                                                            }}
                                                        >
                                                            🗑 Remove
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {/* ── Edit Assignment Modal ── */}
            {editingAssignment && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }} onClick={() => setEditingAssignment(null)}>
                    <div style={{
                        background: '#fff', borderRadius: '16px', padding: '2rem',
                        maxWidth: 500, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
                    }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>✏️ Edit Assignment</h3>
                        <p style={{ color: '#888', fontSize: '0.85rem', margin: '0 0 1.5rem 0' }}>
                            {editingAssignment.subject_name} ({editingAssignment.subject_code})
                        </p>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#555', fontSize: '0.85rem' }}>Teacher</label>
                            <select value={editTeacher} onChange={e => setEditTeacher(e.target.value)}
                                style={{ width: '100%', padding: '10px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '0.9rem' }}>
                                <option value="">Select teacher</option>
                                {teachers.map(t => (
                                    <option key={t.id} value={t.id}>{t.name} {t.designation ? `(${t.designation})` : ''}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#555', fontSize: '0.85rem' }}>Total Marks</label>
                            <input type="number" value={editTotalMarks} onChange={e => setEditTotalMarks(e.target.value)}
                                min="1" style={{ width: '100%', padding: '10px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '1rem', fontWeight: 600, textAlign: 'center' }} />
                        </div>

                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                            <button onClick={() => setEditingAssignment(null)}
                                style={{ padding: '10px 24px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, color: '#555' }}>
                                Cancel
                            </button>
                            <button onClick={handleEditAssignment}
                                style={{ padding: '10px 24px', background: '#238548', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 700, boxShadow: '0 4px 12px rgba(35,133,72,0.3)' }}>
                                💾 Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'publish' && (
                <>
                    {/* ── Step 1: Select Batch ── */}
                    <div style={cardStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.2rem' }}>
                            <span style={{ background: '#238548', color: '#fff', width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem' }}>1</span>
                            <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#333' }}>Select Batch</h3>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '1rem' }}>
                            <div>
                                <label style={labelStyle}>Department *</label>
                                <select value={tab2Dept} onChange={e => { setTab2Dept(e.target.value); setTab2Batch(''); setSubmissionStatus(null); setStudentMarks(null); }} style={selectStyle}>
                                    <option value="">Select Department</option>
                                    {departments.map((d, i) => <option key={i} value={d.value || d.department_name || d.name || d}>{d.label || d.department_name || d.name || d}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={labelStyle}>Year *</label>
                                <select value={tab2Year} onChange={e => { setTab2Year(e.target.value); setTab2Batch(''); setSubmissionStatus(null); setStudentMarks(null); }} style={selectStyle}>
                                    <option value="">Select Year</option>
                                    {yearOptions.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={labelStyle}>Batch *</label>
                                <select value={tab2Batch} onChange={e => { setTab2Batch(e.target.value); setSubmissionStatus(null); setStudentMarks(null); }} style={selectStyle} disabled={!tab2Dept || !tab2Year || tab2BatchesLoading}>
                                    <option value="">{tab2BatchesLoading ? '⏳ Loading...' : (!tab2Dept || !tab2Year) ? 'Select Dept & Year first' : 'Select Batch'}</option>
                                    {tab2Batches.map((b, i) => <option key={i} value={b}>{b}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* ── Step 2: Marks Submission Status ── */}
                    {tab2Batch && (
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.2rem' }}>
                                <span style={{ background: '#238548', color: '#fff', width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem' }}>2</span>
                                <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#333' }}>Marks Submission Status</h3>
                            </div>
                            {statusLoading ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>⏳ Loading submission status...</div>
                            ) : submissionStatus ? (
                                <>
                                    {/* Summary Badges */}
                                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                                        <div style={{ background: '#f0faf3', border: '1px solid #d4edda', borderRadius: '10px', padding: '12px 20px', flex: 1, minWidth: 120, textAlign: 'center' }}>
                                            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#238548' }}>{submissionStatus.summary.totalSubjects}</div>
                                            <div style={{ fontSize: '0.78rem', color: '#666' }}>Total Subjects</div>
                                        </div>
                                        <div style={{ background: '#f0f4ff', border: '1px solid #d4e0ff', borderRadius: '10px', padding: '12px 20px', flex: 1, minWidth: 120, textAlign: 'center' }}>
                                            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#6366f1' }}>{submissionStatus.summary.assignedCount}</div>
                                            <div style={{ fontSize: '0.78rem', color: '#666' }}>Teachers Assigned</div>
                                        </div>
                                        <div style={{ background: submissionStatus.summary.allSubmitted ? '#f0faf3' : '#fff7ed', border: `1px solid ${submissionStatus.summary.allSubmitted ? '#d4edda' : '#fed7aa'}`, borderRadius: '10px', padding: '12px 20px', flex: 1, minWidth: 120, textAlign: 'center' }}>
                                            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: submissionStatus.summary.allSubmitted ? '#238548' : '#f59e0b' }}>{submissionStatus.summary.completedCount}/{submissionStatus.summary.totalSubjects}</div>
                                            <div style={{ fontSize: '0.78rem', color: '#666' }}>Marks Submitted</div>
                                        </div>
                                    </div>

                                    {/* Subject-wise status */}
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                            <thead>
                                                <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                                                    <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#555' }}>Subject</th>
                                                    <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#555' }}>Code</th>
                                                    <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#555' }}>Assigned Teacher</th>
                                                    <th style={{ padding: '10px', textAlign: 'center', fontWeight: 600, color: '#555' }}>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {submissionStatus.subjects.map((s, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                        <td style={{ padding: '10px', fontWeight: 500 }}>{s.subject_name}</td>
                                                        <td style={{ padding: '10px' }}><span style={badgeStyle('#238548')}>{s.subject_code}</span></td>
                                                        <td style={{ padding: '10px' }}>{s.teacher_name || <span style={{ color: '#ccc' }}>Not assigned</span>}</td>
                                                        <td style={{ padding: '10px', textAlign: 'center' }}>
                                                            <span style={badgeStyle(
                                                                s.submission_status === 'completed' ? '#238548'
                                                                    : s.submission_status === 'in_progress' ? '#f59e0b'
                                                                        : s.submission_status === 'assigned' ? '#6366f1'
                                                                            : '#dc2626'
                                                            )}>
                                                                {s.submission_status === 'completed' ? '✅ Submitted'
                                                                    : s.submission_status === 'in_progress' ? '🔄 In Progress'
                                                                        : s.submission_status === 'assigned' ? '📌 Assigned'
                                                                            : '❌ Not Assigned'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Select a batch to see submission status.</div>
                            )}
                        </div>
                    )}

                    {/* ── Step 3: Student Performance ── */}
                    {tab2Batch && submissionStatus && (
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.2rem' }}>
                                <span style={{ background: '#238548', color: '#fff', width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem' }}>3</span>
                                <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#333' }}>Student Performance</h3>
                                {studentMarks && <span style={{ ...badgeStyle('#555'), marginLeft: 'auto' }}>{studentMarks.students.length} students</span>}
                            </div>
                            {marksLoading ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>⏳ Loading student marks...</div>
                            ) : studentMarks && studentMarks.students.length > 0 ? (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                                        <thead>
                                            <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                                                <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>#</th>
                                                <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>Student</th>
                                                <th style={{ padding: '10px', textAlign: 'left', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>ID</th>
                                                {studentMarks.subjects.map(sub => (
                                                    <th key={sub.id} style={{ padding: '10px', textAlign: 'center', fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>{sub.subject_code}</th>
                                                ))}
                                                <th style={{ padding: '10px', textAlign: 'center', fontWeight: 600, color: '#238548', whiteSpace: 'nowrap' }}>Total</th>
                                                <th style={{ padding: '10px', textAlign: 'center', fontWeight: 600, color: '#238548', whiteSpace: 'nowrap' }}>%</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {studentMarks.students.map((st, idx) => (
                                                <tr key={st.student_id} style={{ borderBottom: '1px solid #f0f0f0' }}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#f8fdf9'}
                                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                    <td style={{ padding: '10px', color: '#888' }}>{idx + 1}</td>
                                                    <td style={{ padding: '10px', fontWeight: 500, whiteSpace: 'nowrap' }}>{st.name}</td>
                                                    <td style={{ padding: '10px', fontSize: '0.78rem', color: '#888' }}>{st.student_id}</td>
                                                    {studentMarks.subjects.map(sub => (
                                                        <td key={sub.id} style={{ padding: '10px', textAlign: 'center' }}>
                                                            {st.marks[sub.id] ? st.marks[sub.id].total : 0}
                                                        </td>
                                                    ))}
                                                    <td style={{ padding: '10px', textAlign: 'center', fontWeight: 700, color: '#238548' }}>{st.totalMarks}</td>
                                                    <td style={{ padding: '10px', textAlign: 'center' }}>
                                                        <span style={badgeStyle(parseFloat(st.percentage) >= 50 ? '#238548' : '#dc2626')}>
                                                            {st.percentage}%
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                                    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
                                    No student data available yet.
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Step 4: Publish ── */}
                    {tab2Batch && submissionStatus && studentMarks && studentMarks.students.length > 0 && (
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.2rem' }}>
                                <span style={{ background: '#238548', color: '#fff', width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem' }}>4</span>
                                <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#333' }}>Publish Marksheets</h3>
                            </div>
                            <div style={{ background: '#f8fdf9', borderRadius: '10px', padding: '1rem', border: '1px solid #d4edda', marginBottom: '1rem', fontSize: '0.85rem', color: '#333' }}>
                                📋 <strong>{studentMarks.students.length}</strong> students will receive a marksheet. Enter a title below (e.g., <em>"3rd Sem Marksheet"</em>) — students will see this title and can download their individual marksheet.
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '1rem', alignItems: 'end' }}>
                                <div>
                                    <label style={labelStyle}>Marksheet Title *</label>
                                    <input
                                        type="text"
                                        value={publishTitle}
                                        onChange={e => setPublishTitle(e.target.value)}
                                        placeholder='e.g. 3rd Sem Marksheet 2025-26'
                                        style={{ ...selectStyle, cursor: 'text' }}
                                    />
                                </div>
                                <div>
                                    <button
                                        onClick={handlePublish}
                                        disabled={!publishTitle.trim() || publishing}
                                        style={{
                                            width: '100%', padding: '12px 24px',
                                            background: !publishTitle.trim() ? '#ccc' : 'linear-gradient(135deg, #238548, #2ea35e)',
                                            color: '#fff', border: 'none', borderRadius: '10px',
                                            fontWeight: 700, fontSize: '0.9rem',
                                            cursor: !publishTitle.trim() ? 'not-allowed' : 'pointer',
                                            boxShadow: publishTitle.trim() ? '0 4px 12px rgba(35,133,72,0.3)' : 'none',
                                            opacity: publishing ? 0.7 : 1,
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {publishing ? '⏳ Publishing...' : '📢 Publish Marksheets'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </Layout>
    );
};

export default AdminMarksCardAssign;
